import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Home, ShoppingBag, CalendarDays, Users, Phone, MapPin, CheckCircle2, Send, Smile, HandHelping, Anchor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SmartAnfrageForm from "@/components/SmartAnfrageForm";
import Header from "@/components/Header";
import BewerbenCTA from "@/components/BewerbenCTA";

const LEISTUNGEN = [
  { icon: Home, title: "Haushaltshilfe in Rheinberg", desc: "Unterstützung bei der Haushaltsführung in der Rheinberger Innenstadt, in Orsoy, Budberg, Borth und Ossenberg — damit Ihr Zuhause gepflegt bleibt." },
  { icon: ShoppingBag, title: "Einkaufen & Besorgungen", desc: "Begleitung zum Einkaufen auf dem Rheinberger Wochenmarkt, im Underberg-Center oder zu Geschäften in Budberg und Borth. Oder wir erledigen die Einkäufe für Sie." },
  { icon: CalendarDays, title: "Begleitung zu Terminen", desc: "Arztbesuche am St. Josef Krankenhaus Rheinberg, Facharzttermine in Moers oder Duisburg, Behördengänge — wir begleiten Sie sicher und zuverlässig." },
  { icon: Smile, title: "Gesellschaft & Freizeitgestaltung", desc: "Spaziergänge am Rheinufer bei Orsoy, ein Besuch in der historischen Altstadt oder gemütliche Nachmittage bei Ihnen zuhause — gegen Einsamkeit im Alltag." },
  { icon: Users, title: "Entlastung für Angehörige", desc: "Pflegende Angehörige in Rheinberg brauchen auch Pausen. Wir übernehmen stundenweise die Betreuung, damit Sie durchatmen können." },
  { icon: HandHelping, title: "Tagesstruktur & Routinen", desc: "Unterstützung bei der Tagesplanung, Medikamentenerinnerung und Begleitung durch den Alltag — persönlich und verlässlich an Ihrer Seite." },
];

const FAQ_ITEMS = [
  { q: "Bieten Sie Alltagsbegleitung in ganz Rheinberg an?", a: "Ja, wir sind im gesamten Stadtgebiet aktiv: Rheinberg-Innenstadt, Orsoy, Budberg, Borth, Ossenberg und Vierbaum. Kontaktieren Sie uns für ein unverbindliches Erstgespräch." },
  { q: "Was kostet die Alltagsbegleitung in Rheinberg?", a: "Für Personen mit Pflegegrad 1-5 werden die Kosten vollständig über den Entlastungsbetrag (131 € monatlich) Ihrer Pflegekasse übernommen. Sie zahlen keinen Eigenanteil." },
  { q: "Wie weit ist es von Ihrem Büro nach Rheinberg?", a: "Unser Büro in Grefrath (Hochstr. 72) liegt etwa 25 Minuten von Rheinberg entfernt. Wir betreuen bereits regelmäßig Klienten in Rheinberg und kennen die Region gut." },
  { q: "Kann die Alltagsbegleitung kurzfristig beginnen?", a: "In der Regel können wir innerhalb weniger Tage nach Ihrer Anfrage starten. Bei dringenden Situationen versuchen wir, schnellstmöglich einen Termin zu finden." },
  { q: "Brauche ich einen Pflegegrad für die Alltagsbegleitung?", a: "Um die Kosten über den Entlastungsbetrag abrechnen zu können, benötigen Sie einen anerkannten Pflegegrad (mindestens Pflegegrad 1). Wir beraten Sie gerne auch zur Beantragung." },
  { q: "Kann ich Alltagsbegleitung mit einem Pflegedienst kombinieren?", a: "Ja, das ergänzt sich ideal. Der ambulante Pflegedienst übernimmt medizinisch-pflegerische Aufgaben, unsere Alltagsbegleitung kümmert sich um Haushalt, Begleitung und Gesellschaft." },
];

export default function AlltagsbegleitungRheinberg() {

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Alltagsbegleitung Rheinberg | Haushaltshilfe & Betreuung | Lehnen & Weinacht";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Alltagsbegleitung in Rheinberg: Haushaltshilfe, Einkaufsbegleitung, Arztbegleitung und Entlastung für Angehörige. In Orsoy, Budberg, Borth & Ossenberg. Über den Entlastungsbetrag finanzierbar.");

    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.id = "schema-rheinberg";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Lehnen & Weinacht – Alltagsbegleitung Rheinberg",
      "description": "Professionelle Alltagsbegleitung in Rheinberg: Haushaltshilfe, Einkaufsbegleitung, Betreuung und Entlastung für pflegende Angehörige.",
      "url": window.location.origin + "/alltagsbegleitung-rheinberg",
      "telephone": "+491637917044",
      "email": "lehnenundweinacht@gmail.com",
      "address": { "@type": "PostalAddress", "streetAddress": "Hochstr. 72", "addressLocality": "Grefrath", "postalCode": "47929", "addressRegion": "NRW", "addressCountry": "DE" },
      "areaServed": [{ "@type": "City", "name": "Rheinberg" }, { "@type": "City", "name": "Orsoy" }, { "@type": "City", "name": "Budberg" }, { "@type": "City", "name": "Borth" }],
      "priceRange": "Über Entlastungsbetrag finanzierbar",
      "openingHours": "Mo-Fr 08:00-18:00"
    });
    document.head.appendChild(schema);
    return () => { const el = document.getElementById("schema-rheinberg"); if (el) el.remove(); };
  }, []);


  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="alltagsbegleitung-rheinberg-page">
      <Header />

      {/* Hero – „Sanfter Verlauf“: Text links, Standortbild rechts, weicher Verlauf verbindet beides */}
      <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-20 overflow-hidden" data-testid="rheinberg-hero">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 w-[500px] h-[500px] bg-green-50/50 rounded-full blur-3xl -translate-y-1/3 -translate-x-1/2" />
        </div>
        {/* Desktop: Bild rechts als Fläche, links sanft in den Seitenhintergrund auslaufend */}
        <figure className="hidden lg:block absolute inset-y-0 right-0 w-[52%] m-0" data-testid="rheinberg-standortbild-desktop">
          <img src="/standort-rheinberg-visualisierung.jpg" alt="Visualisierung des geplanten Standorts von Lehnen & Weinacht in Rheinberg: gelbe Fassade mit Giebelfenstern, Firmenschriftzug und Schaufenstern" className="absolute inset-0 h-full w-full object-cover object-center" loading="eager" fetchPriority="high" />
          <div className="absolute inset-y-0 left-0 w-[45%] bg-gradient-to-r from-[#F8FAFC] via-[#F8FAFC]/75 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#F8FAFC]/90 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
          <figcaption className="absolute top-20 right-4 rounded-md bg-slate-900/70 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm" data-testid="rheinberg-bild-kennzeichnung-desktop">Visualisierung – geplante Gestaltung</figcaption>
        </figure>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-cyan-700 transition-colors">Startseite</Link>
            <span>/</span>
            <Link to="/alltagsbegleitung" className="hover:text-cyan-700 transition-colors">Alltagsbegleitung</Link>
            <span>/</span>
            <span className="text-slate-600">Rheinberg</span>
          </nav>

          <div className="max-w-4xl lg:max-w-[48%]">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-100 rounded-full mb-4">
              <Anchor className="h-4 w-4 text-green-700" />
              <span className="text-sm font-semibold text-green-800">Alltagsbegleitung am Niederrhein in Rheinberg</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2.5 bg-cyan-700 text-white rounded-xl mb-6 w-fit shadow-md shadow-cyan-700/20" data-testid="rheinberg-eroeffnung-hinweis">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="text-sm sm:text-base font-bold">Neuer Standort Rheinberg – Eröffnung am 01.01.2027</span>
            </div>
            <h1 className="font-['Manrope'] text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5">
              Alltagsbegleitung<br />
              <span className="text-green-700">in Rheinberg</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-3xl leading-relaxed mb-4">
              Persönliche Unterstützung im Alltag für Pflegebedürftige und Angehörige in Rheinberg, Orsoy, Budberg und Borth. Haushaltshilfe, Begleitung und Betreuung — finanziert über den Entlastungsbetrag Ihrer Pflegekasse.
            </p>
            <p className="text-base text-slate-600 max-w-3xl leading-relaxed mb-8" data-testid="rheinberg-eroeffnung-text">
              Ab dem 01.01.2027 werden wir mit einem eigenen Standort in Rheinberg für Sie da sein. Bis dahin erreichen Sie uns wie gewohnt telefonisch oder über das Anfrageformular — wir betreuen Rheinberg bereits heute von Grefrath aus.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#anfrage-rheinberg" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-8 py-4 text-lg font-semibold transition-all active:scale-[0.98] shadow-lg" data-testid="rheinberg-cta-top">
                Jetzt in Rheinberg anfragen
              </a>
              <a href="tel:+491637917044" className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 hover:border-green-300 text-slate-700 rounded-xl px-8 py-4 text-lg font-semibold transition-all">
                <Phone className="h-5 w-5" /> 0163 791 7044
              </a>
            </div>
          </div>

          {/* Smartphone/Tablet: Bild unter dem Text, ungeschnitten im Originalverhältnis */}
          <figure className="lg:hidden mt-10 m-0" data-testid="rheinberg-standortbild-mobil">
            <div className="relative rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-200 bg-white">
              <img src="/standort-rheinberg-visualisierung-800.jpg" srcSet="/standort-rheinberg-visualisierung-800.jpg 800w, /standort-rheinberg-visualisierung.jpg 1400w" sizes="(max-width: 1023px) 100vw, 50vw" alt="Visualisierung des geplanten Standorts von Lehnen & Weinacht in Rheinberg: gelbe Fassade mit Giebelfenstern, Firmenschriftzug und Schaufenstern" className="block w-full h-auto aspect-[1463/1075] object-cover" loading="lazy" />
              <div className="absolute top-3 left-3 rounded-lg bg-cyan-700 text-white text-xs font-bold px-2.5 py-1.5 shadow">Eröffnung am 01.01.2027</div>
            </div>
            <figcaption className="mt-2 text-xs text-slate-500 text-center" data-testid="rheinberg-bild-kennzeichnung-mobil">Neuer Standort Rheinberg · Visualisierung – geplante Gestaltung</figcaption>
          </figure>
        </div>
      </section>

      {/* Rheinberg-spezifisch */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-6">Alltagsbegleitung in der Stadt am Rhein</h2>
          <div className="space-y-4 text-base text-slate-600 leading-relaxed">
            <p>
              Rheinberg — die Stadt am Rhein mit ihrer historischen Altstadt und dem charmanten Ortsteil Orsoy — ist Heimat für rund 31.000 Menschen. Viele ältere Rheinberger schätzen die Ruhe und Lebensqualität ihrer Heimat, möchten so lange wie möglich selbstbestimmt in ihren eigenen vier Wänden leben.
            </p>
            <p>
              Doch wenn alltägliche Aufgaben schwerer fallen — der Gang zum Arzt, der Wocheneinkauf oder einfach die Hausarbeit — braucht es eine helfende Hand. Unsere Alltagsbegleitung bietet genau diese Unterstützung: <strong>persönlich, verlässlich und direkt bei Ihnen vor Ort in Rheinberg</strong>.
            </p>
            <p>
              Als <strong>anerkanntes Angebot zur Unterstützung im Alltag</strong> nach § 45a SGB XI werden unsere Leistungen vollständig über den Entlastungsbetrag Ihrer Pflegekasse finanziert. Für Sie entstehen keine Eigenkosten.
            </p>
          </div>

          {/* Stadtteile Grid */}
          <div className="mt-10">
            <h3 className="font-['Manrope'] text-lg font-bold text-slate-900 mb-4">Unsere Einsatzgebiete in Rheinberg</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {["Innenstadt", "Orsoy", "Budberg", "Borth", "Ossenberg"].map(st => (
                <div key={st} className="bg-green-50 rounded-xl p-4 border border-green-100 text-center">
                  <MapPin className="h-5 w-5 text-green-600 mx-auto mb-1" />
                  <p className="font-semibold text-slate-800 text-sm">{st}</p>
                  <p className="text-xs text-green-600 mt-0.5">Verfügbar</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="rheinberg-leistungen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Unsere Leistungen in Rheinberg</h2>
            <p className="text-base text-slate-500 max-w-2xl mx-auto">Alltagsunterstützung, die sich Ihrem Leben anpasst — in allen Stadtteilen von Rheinberg.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {LEISTUNGEN.map((l, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`rheinberg-leistung-${i}`}>
                <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <l.icon className="h-6 w-6 text-green-700" />
                </div>
                <h3 className="font-['Manrope'] text-lg font-bold text-slate-900 mb-2">{l.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Finanzierung */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-50 rounded-2xl border border-green-200 p-8 sm:p-10">
            <h2 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-4">Kostenübernahme durch Ihre Pflegekasse</h2>
            <p className="text-base text-slate-600 leading-relaxed mb-6">
              Unsere Alltagsbegleitung in Rheinberg wird vollständig über den <strong>Entlastungsbetrag</strong> (131 € monatlich) Ihrer Pflegekasse finanziert. Wir sind nach § 45a SGB XI als Angebot zur Unterstützung im Alltag anerkannt — Sie zahlen <strong>keinen Eigenanteil</strong>.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-4 border border-green-100 text-center">
                <p className="text-2xl font-extrabold text-green-700 font-['Manrope']">131 €</p>
                <p className="text-xs text-slate-500 mt-1">monatlicher Entlastungsbetrag</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-green-100 text-center">
                <p className="text-2xl font-extrabold text-green-700 font-['Manrope']">Ab PG 1</p>
                <p className="text-xs text-slate-500 mt-1">bereits ab Pflegegrad 1</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-green-100 text-center">
                <p className="text-2xl font-extrabold text-green-700 font-['Manrope']">0 €</p>
                <p className="text-xs text-slate-500 mt-1">Eigenanteil für Sie</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="rheinberg-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">Häufige Fragen zur Alltagsbegleitung in Rheinberg</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-slate-200/60 rounded-xl px-5 shadow-sm">
                <AccordionTrigger className="text-base font-semibold text-slate-800 hover:text-green-700 hover:no-underline py-4">{item.q}</AccordionTrigger>
                <AccordionContent className="text-base text-slate-600 leading-relaxed pb-4">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-['Manrope'] text-lg font-bold text-slate-900 mb-4">Alltagsbegleitung auch in Ihrer Nähe</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Link to="/alltagsbegleitung-grefrath" className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:shadow-md transition-all text-center group">
              <MapPin className="h-5 w-5 text-green-600 mx-auto mb-1" />
              <p className="font-semibold text-slate-800 group-hover:text-green-700 text-sm">Grefrath</p>
            </Link>
            <Link to="/alltagsbegleitung" className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:shadow-md transition-all text-center group">
              <Heart className="h-5 w-5 text-green-600 mx-auto mb-1" />
              <p className="font-semibold text-slate-800 group-hover:text-green-700 text-sm">Alle Gebiete</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Anfrage */}
      <BewerbenCTA stadtName="Rheinberg" stadtSlug="rheinberg" />
      <section className="py-16 bg-[#F8FAFC]" id="anfrage-rheinberg" data-testid="rheinberg-anfrage">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <SmartAnfrageForm stadtName="Rheinberg" stadtSlug="rheinberg" testIdPrefix="rheinberg" showHeadline />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} Lehnen & Weinacht — Pflegeberatung & Alltagsbegleitung</p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-3 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <Link to="/alltagsbegleitung" className="hover:text-white transition-colors">Alltagsbegleitung</Link>
            <Link to="/alltagsbegleitung-grefrath" className="hover:text-white transition-colors">Grefrath</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Ratgeber</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
